import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bookmark } from "lucide-react";
import "./loiresult.css";
import Pagination from "../../pagination";
import FiltreLois from "../FiltreLois/FiltreLois";

const Loiresults = ({ data, searchQuery, onPageChange }) => {
    const [activeTab, setActiveTab] = useState({});
    const [bookmarkedItems, setBookmarkedItems] = useState([]);
    const [searchData, setSearchData] = useState(data);

    const handleTabChange = (resultId, tab) => {
        setActiveTab(prev => ({
            ...prev,
            [resultId]: tab
        }));
    };

    const handleBookmarkClick = (id) => {
        setBookmarkedItems(prev => {
            if (prev.includes(id)) {
                return prev.filter(itemId => itemId !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const handlePageChange = (page) => {
        if (onPageChange) {
            onPageChange(page);
        }
    };

    const cleanEnrichedContent = (text) => {
        if (!text) return "";

        // Supprimer tout le contenu entre deux balises (titleenrichi), y compris les balises elles-mêmes
        let cleanedText = text.replace(/\(titleenrichi\).*?\(titleenrichi\)/gs, '');

        // Supprimer toute balise (titleenrichi) restante au cas où il y en aurait des orphelines
        cleanedText = cleanedText.replace(/\(titleenrichi\)/g, '');

        // Nettoyer les lignes vides multiples et les espaces inutiles
        cleanedText = cleanedText.replace(/\n\s*\n/g, '\n').trim();

        return cleanedText;
    };

    const highlightKeywords = (text, keywords) => {
        if (!text) return "";
        if (!keywords || keywords.length === 0) return text;

        let termsToHighlight = [...keywords];
        if (searchQuery) {
            const searchTerms = searchQuery.toLowerCase().split(/\s+/).filter(term => term.length > 2);
            searchTerms.forEach(term => {
                if (!termsToHighlight.includes(term)) {
                    termsToHighlight.push(term);
                }
            });
        }

        if (termsToHighlight.length === 0) return text;

        const escapedTerms = termsToHighlight.map(term =>
            term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        );
        const regex = new RegExp(`(${escapedTerms.join('|')})`, 'gi');

        return text.replace(regex, (match) => `<mark>${match}</mark>`);
    };

    return (
        <div className="result-list">
            <div className="result-count">
                <h2>{searchData.count} Résultat{searchData.count > 1 ? 's' : ''} trouvé{searchData.count > 1 ? 's' : ''}</h2>
                <div className="sort-options">
                    <span>Trier par</span>
                    <select className="sort-select">
                        <option value="pertinence">Pertinence</option>
                        <option value="date">Date</option>
                        <option value="code">Code</option>
                    </select>
                </div>
            </div>

            <div className="results">
                {searchData.results.map((result, index) => {
                    const isBookmarked = bookmarkedItems.includes(result.id);
                    const keywords = result.mots_cles || ['contrat', 'civil', 'obligation'];

                    return (
                        <div key={result.id} className="result-item">
                            <div className="result-header">
                                <h3 className="result-title">
                                    {index + 1}. {result.number} - {result.legal_text_title}
                                </h3>

                                {result.statut && (
                                    <div className="status-badge">
                                        <span className={`status-label ${result.statut.toLowerCase()}`}>
                                            En {result.statut}
                                        </span>
                                    </div>
                                )}

                                <button
                                    className={`bookmark-button ${isBookmarked ? 'active' : ''}`}
                                    onClick={() => handleBookmarkClick(result.id)}
                                >
                                    <Bookmark size={20} />
                                </button>

                                <div className="result-meta">
                                    Entré en vigueur le {result.date_entree_vigueur}
                                </div>

                                <div className="result-links-top">
                                    <button
                                        className={`result-link ${(!activeTab[result.id] || activeTab[result.id] === 'contenu') ? 'active' : ''}`}
                                        onClick={() => handleTabChange(result.id, 'contenu')}
                                        style={{
                                            borderBottom: (!activeTab[result.id] || activeTab[result.id] === 'contenu') ? '2px solid #F57C00' : 'none'
                                        }}
                                    >
                                        Contenu
                                    </button>
                                    <button
                                        className={`result-link ${activeTab[result.id] === 'historique' ? 'active' : ''}`}
                                        onClick={() => handleTabChange(result.id, 'historique')}
                                        style={{
                                            borderBottom: activeTab[result.id] === 'historique' ? '2px solid #F57C00' : 'none'
                                        }}
                                    >
                                        Historique
                                    </button>
                                </div>
                            </div>

                            <div className="result-content">
                                {(!activeTab[result.id] || activeTab[result.id] === 'contenu') ? (
                                    <p
                                        className="result-text"
                                        dangerouslySetInnerHTML={{
                                            __html: highlightKeywords(
                                                cleanEnrichedContent(result.content),
                                                keywords
                                            ),
                                        }}
                                    />
                                ) : (
                                    <div className="result-history">
                                        <ul>
                                            <li>Créé par Ordonnance n°2016-131 du 10 février 2016 - art. 2</li>
                                            <li>{result.legal_text_title}</li>
                                        </ul>
                                    </div>
                                )}
                            </div>

                            <div className="result-footer">
                                <div className="keywords-container">
                                    <span className="mots-cles-label">Mots-clés:</span>
                                    <div className="result-keywords">
                                        {keywords.map((keyword, i) => (
                                            <span key={i} className="keyword-badge">
                                                {keyword}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <Link
                                    to={`/article/${result.id}?legal_text_id=${result.legal_text_id || ''}`}
                                    className="result-full-link"
                                >
                                    <button className="btn-consult">
                                        Lire l'article
                                        <ArrowRight size={16} className="icon-arrow" />
                                    </button>
                                </Link>
                            </div>
                        </div>
                    );
                })}
            </div>

            <Pagination
                data={searchData}
                currentPage={parseInt(window.location.search.split('page=')[1]?.split('&')[0] || '1', 10)}
                onPageChange={handlePageChange}
                searchQuery={searchQuery}
            />
        </div>
    );
};

export default Loiresults;